/**
 * Icon registry.
 *
 * Hand-authored 24×24 stroke paths, no icon dependency. Each entry is a single
 * `d` string in which every `M` starts a new subpath; the component splits on
 * `M` and emits one <path> each, which keeps `stroke-linecap: round` from
 * joining unrelated strokes.
 *
 * An unknown name renders the `info` glyph rather than nothing, so a typo is
 * visible in review instead of silently collapsing a layout.
 */

import type { JSX } from 'react';

export const ICONS: Readonly<Record<string, string>> = {
  home: 'M3 10.5 12 3l9 7.5V21H3z',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3',
  calendar:
    'M8 2v4M16 2v4M3 8h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  euro: 'M18 6.7A7 7 0 1 0 18 17.3M4 10h9M4 14h9',
  filter: 'M3 5h18l-7 8v6l-4-2v-4z',
  refresh: 'M21 12a9 9 0 1 1-3-6.7M21 4v5h-5',
  settings:
    'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2v.2a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-2.9-1.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3 15H3a2 2 0 1 1 0-4h.2A1.7 1.7 0 0 0 4.5 8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 10 4.6V4a2 2 0 1 1 4 0v.2a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1A1.7 1.7 0 0 0 21 11.4h.2a2 2 0 1 1 0 4H21a1.7 1.7 0 0 0-1.6 1z',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  users:
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  bot: 'M12 8V4M5 8h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2zM9 13h.01M15 13h.01M9 17h6',
  sparkles:
    'M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6zM19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z',
  trophy: 'M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0zM7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3',
  goal: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  clipboard:
    'M9 3h6v3H9zM8 5H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M8 11h8M8 15h8M8 19h5',
  handshake:
    'M11 17 8.5 14.5a2 2 0 0 1 0-2.8l3-3a2 2 0 0 1 2.8 0L17 11M3 11l4-4h4l3 3M21 13l-4 4h-4l-3-3',
  dumbbell: 'M6.5 6.5v11M17.5 6.5v11M3 9v6M21 9v6M6.5 12h11',
  arrowR: 'M5 12h14M13 5l7 7-7 7',
  arrowL: 'M19 12H5M11 19l-7-7 7-7',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  check: 'M20 6 9 17l-5-5',
  checkCircle: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8.5 12.5 11 15l4.5-5',
  alert: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 8v5M12 16h.01',
  warn: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01',
  x: 'M18 6 6 18M6 6l12 12',
  chevD: 'M6 9l6 6 6-6',
  chevR: 'M9 6l6 6-6 6',
  download: 'M12 3v12M7 11l5 5 5-5M4 21h16',
  upload: 'M12 17V5M7 9l5-5 5 5M4 21h16',
  eye: 'M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  eyeOff:
    'M3 3l18 18M10.6 6.2A9.8 9.8 0 0 1 12 6c6.4 0 10 6 10 6a17 17 0 0 1-2.8 3.4M6.3 8A17 17 0 0 0 2 12s3.6 6 10 6c1 0 2-.1 2.8-.4',
  edit: 'M4 20h4L20 8a2.8 2.8 0 0 0-4-4L4 16zM14 6l4 4',
  copy: 'M9 9h10v10a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V9zM5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1',
  share:
    'M15 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM15 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM8.6 13.5l5.8 3.3M14.4 7.2 8.6 10.5',
  archive: 'M3 4h18v4H3zM5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8M9 12h6',
  trash:
    'M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13',
  file: 'M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7zM14 2v5h5M9 13h6M9 17h6',
  fileCheck: 'M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7zM14 2v5h5M9 15l2 2 4-4',
  fileSign: 'M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7zM14 2v5h5M8 18c2-3 4 1 6-2',
  shield: 'M12 21s8-3.5 8-9V5l-8-3-8 3v7c0 5.5 8 9 8 9z',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  unlock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 7.5-2',
  database:
    'M12 8c4.4 0 8-1.3 8-3s-3.6-3-8-3-8 1.3-8 3 3.6 3 8 3zM4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
  barChart: 'M4 20V10M10 20V4M16 20v-8M22 20H2',
  lineChart: 'M3 3v18h18M7 15l4-5 3 3 5-7',
  trendUp: 'M3 17l6-6 4 4 8-8M21 7v6h-6',
  building:
    'M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16M16 9h3a2 2 0 0 1 2 2v10M8 7h4M8 11h4M8 15h4M4 21h17',
  compass: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM15.5 8.5l-2 5-5 2 2-5z',
  layers: 'M12 2 2 7l10 5 10-5zM2 12l10 5 10-5M2 17l10 5 10-5',
  radio:
    'M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM7.8 7.8a6 6 0 0 0 0 8.4M16.2 16.2a6 6 0 0 0 0-8.4M4.9 4.9a10 10 0 0 0 0 14.2M19.1 19.1a10 10 0 0 0 0-14.2',
  network:
    'M12 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM5 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM12 8v5M12 13H5v3M12 13h7v3',
  branch:
    'M6 3v12M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 9v1a5 5 0 0 1-5 5H6',
  target:
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  dollar:
    'M12 2v20M17 6.5C17 4.6 14.8 3.5 12 3.5S7 4.6 7 6.5s2 2.8 5 3.5 5 1.6 5 3.5-2.2 3-5 3-5-1.1-5-3',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3.5 2',
  play: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM10 8.5l6 3.5-6 3.5z',
  more: 'M12 6h.01M12 12h.01M12 18h.01',
  grip: 'M9 5h.01M9 12h.01M9 19h.01M15 5h.01M15 12h.01M15 19h.01',
  star: 'M12 3l2.7 5.6 6.3.9-4.5 4.4 1 6.1-5.5-2.9L6.5 20l1-6.1L3 9.5l6.3-.9z',
  flame:
    'M12 22c4 0 7-2.7 7-6.5 0-4.5-4-6-5-10.5-2 2-3 3.5-3 5.5C9 8 8 7 7 5.5 6 8 5 10 5 12.5 5 17 8 22 12 22z',
  crown: 'M3 17h18l-2-9-4 4-3-6-3 6-4-4zM4 21h16',
  medal:
    'M12 21a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM12 17.5l-2 1 .5-2.3-1.7-1.6 2.3-.3 1-2 .9 2 2.3.3-1.7 1.6.5 2.3zM8 3l2 5M16 3l-2 5',
  activity: 'M22 12h-4l-3 8-4-16-3 8H2',
  briefcase:
    'M4 8h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM9 8V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3M3 13h18',
  scroll:
    'M6 3h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM8 8h8M8 12h8M8 16h5',
  mega: 'M3 11v3l14 5V6zM17 9a3 3 0 0 1 0 6M6 14v5h3',
  present: 'M2 4h20M4 4v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4M12 16v4M9 22l3-2 3 2',
  mic: 'M12 15a4 4 0 0 0 4-4V7a4 4 0 0 0-8 0v4a4 4 0 0 0 4 4zM5 11a7 7 0 0 0 14 0M12 18v4M8 22h8',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  clip: 'M21 11.5 12.5 20a5 5 0 0 1-7-7l8-8a3.5 3.5 0 0 1 5 5l-8 8a2 2 0 0 1-3-3l7-7',
  library: 'M4 4h4v16H4zM10 4h4v16h-4zM17 5l3 14',
  msg: 'M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z',
  wand: 'M15 4V2M15 10V8M12 6h-2M20 6h-2M17.5 3.5 16 5M17.5 8.5 16 7M12.5 3.5 14 5M3 21 14 10',
  tv: 'M4 7h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1zM8 3l4 4 4-4',
  monitor: 'M3 4h18v12H3zM8 20h8M12 16v4',
  globe:
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c2.5 2.6 2.5 15.4 0 18M12 3c-2.5 2.6-2.5 15.4 0 18',
  news: 'M4 5h13a1 1 0 0 1 1 1v13a2 2 0 0 0 2-2V8h-2M4 5a1 1 0 0 0-1 1v11a2 2 0 0 0 2 2h13M7 9h7M7 13h7',
  image: 'M4 4h16v16H4zM9 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM5 19l5-6 4 4 2-2 3 4',
  thumb:
    'M7 22H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h2M7 11l4-8a2 2 0 0 1 3 2v4h4a2 2 0 0 1 2 2.4l-1.4 6A2 2 0 0 1 16.6 22H7z',
  heart:
    'M20.8 5.6a5 5 0 0 0-7.1 0L12 7.3l-1.7-1.7a5 5 0 0 0-7.1 7.1L12 21l8.8-8.3a5 5 0 0 0 0-7.1z',
  award: 'M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM8.2 13.8 7 22l5-3 5 3-1.2-8.2',
  bulb: 'M9 18h6M10 22h4M12 2a6 6 0 0 0-4 10.5c.7.8 1 1.6 1 2.5h6c0-.9.3-1.7 1-2.5A6 6 0 0 0 12 2z',
  quote: 'M9 7H5v5h4l-1 5M19 7h-4v5h4l-1 5',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  gridI: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  extLink: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3',
  save: 'M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2zM7 3v6h8M7 15h10',
  print: 'M6 9V3h12v6M6 18H4v-6h16v6h-2M8 14h8v7H8z',
  zap: 'M13 2 4 14h7l-1 8 9-12h-7z',
  gauge: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 12l4-4',
  scale: 'M12 3v18M6 7l-3 8h6zM18 7l-3 8h6zM6 7h12',
  spark2: 'M3 12h4l3-8 4 16 3-8h4',
  book: 'M4 4h7a2 2 0 0 1 2 2v14a2 2 0 0 0-2-2H4zM20 4h-7a2 2 0 0 0-2 2v14a2 2 0 0 1 2-2h7z',
  flag: 'M4 21V4h11l-1 3 6 1-1 6H4',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v5M12 8h.01',
  pkg: 'M20 8 12 3 4 8v8l8 5 8-5zM4 8l8 5 8-5M12 13v8',
};

export type IconName = string;

/** Rendered when a name is not in the registry, so a typo is visible. */
const FALLBACK_PATH = ICONS.info ?? 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z';

export interface IconProps {
  readonly name: IconName;
  readonly className?: string | undefined;
  /** Provide when the icon is the only label; omit for decorative icons. */
  readonly title?: string | undefined;
}

export function Icon({ name, className, title }: IconProps): JSX.Element {
  const d = ICONS[name] ?? FALLBACK_PATH;
  const subpaths = d.split('M').filter(Boolean);
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`i ${className ?? ''}`}
      role={title === undefined ? 'presentation' : 'img'}
      aria-hidden={title === undefined ? true : undefined}
      aria-label={title}
    >
      {title !== undefined && <title>{title}</title>}
      {subpaths.map((p, i) => (
        <path key={i} d={`M${p}`} />
      ))}
    </svg>
  );
}
